import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchForms, deleteForm } from '@/store/slices/formSlice';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Edit, Trash2, Eye, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

const FormsList = () => {
  const dispatch = useDispatch();
  const { forms, loading } = useSelector(state => state.forms);

  useEffect(() => {
    dispatch(fetchForms());
  }, [dispatch]);

  const handleDelete = async (formId) => {
    if (window.confirm('Are you sure you want to delete this form?')) {
      await dispatch(deleteForm(formId));
      dispatch(fetchForms()); // Refresh the list
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge variant="default">Active</Badge>
    ) : (
      <Badge variant="secondary">Inactive</Badge>
    );
  };

  if (loading) {
    return <div className="flex justify-center py-8"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Forms Management</h1>
        <Button asChild>
          <Link to="/admin/forms/create">
            <Plus className="h-4 w-4 mr-2" />
            Create Form
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Forms</CardTitle>
        </CardHeader>
        <CardContent>
          {forms.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No forms created yet. Click "Create Form" to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Fields</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Verification</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {forms.map((form) => (
                  <TableRow key={form.id}>
                    <TableCell className="font-medium">{form.name}</TableCell>
                    <TableCell className="max-w-xs truncate">
                      {form.description || 'No description'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{form.fields?.length || 0} fields</Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(form.is_active)}</TableCell>
                    <TableCell>
                      {form.requires_verification ? (
                        <Badge variant="secondary">Required</Badge>
                      ) : (
                        <Badge variant="outline">Not Required</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="icon" asChild>
                          <Link to={`/admin/forms/${form.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="outline" size="icon" asChild>
                          <Link to={`/admin/forms/${form.id}/edit`}>
                            <Edit className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="destructive"
                          size="icon"
                          onClick={() => handleDelete(form.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FormsList;